/**
 * Landing outcomes (v1 plan section 7.6), read off the attitude at touchdown.
 * The legs absorb a forward spin up to a rate that falls with the tuck; past it
 * the body rolls over its rolling contact and keeps its speed. Under it, a fall
 * of the mass past rollDropHeight is a hard landing that bleeds speed. Nothing
 * here is a button: a roll is a tuck and a forward rotation meeting the ground.
 */

import { comHeight } from "../body";
import type { EventBuffer } from "../events";
import { abs, HALF_PI, RAD_TO_DEG, sign, TWO_PI } from "../fastmath";
import { emitGrade, gradeBand } from "../grades";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { Ev, Grade, GradeKind, S } from "../types";
import { setState } from "./transitions";

/** Track the highest point of the mass since the last contact. Call every tick after the move. */
export function trackApex(s: SimState, t: CompiledTuning): void {
  const mass = s.y + comHeight(t, s.tuck);
  if (s.grounded === 1 || s.barId >= 0 || s.ledgeSolid >= 0 || mass > s.apexY) s.apexY = mass;
}

/** The forward spin the legs can absorb at this tuck, rad/s. */
export function rollThreshold(t: CompiledTuning, tuck: number): number {
  return t.rollSpinUntucked + (t.rollSpinTucked - t.rollSpinUntucked) * tuck;
}

/**
 * At touchdown, after the impact has pitched the body: roll, hard landing, or an ordinary landing.
 * `spinBefore` is the forward spin before the impact's pitch, to tell a roll the player made from one
 * the impact alone produced. `apexBefore` is the highest mass height of the flight.
 */
export function touchdown(
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  spinBefore: number,
  apexBefore: number,
): void {
  const dir = sign(s.vx);
  const drop = apexBefore - (s.y + comHeight(t, s.tuck));
  const forward = s.phiDot * dir;
  const threshold = rollThreshold(t, s.tuck);
  if (dir !== 0 && forward > threshold) {
    const pitchDeg = s.phi * dir * RAD_TO_DEG;
    const impactOnly = spinBefore * dir <= threshold;
    const grade = impactOnly
      ? Grade.Ok
      : gradeBand(pitchDeg, t.rollPerfectMin, t.rollPerfectMax, t.rollGoodMin, t.rollGoodMax);
    s.tumbling = 1;
    s.rollDir = dir;
    // The turn counts from upright: a body that lands pitched 60 degrees forward is 60 degrees in, and
    // one already past upside down (phi wrapped to the other sign) is more than half way round.
    s.rollTurn = s.phi * dir;
    if (s.rollTurn < -HALF_PI) s.rollTurn += TWO_PI;
    if (grade === Grade.Perfect) s.vx += dir * t.rollPerfectBoost;
    s.lastRollGrade = grade;
    ev.push(Ev.Rolled, s.tick, grade, pitchDeg, forward);
    emitGrade(s, t, ev, GradeKind.Roll, grade, pitchDeg);
    setState(s, ev, S.Roll);
    return;
  }
  if (drop >= t.rollDropHeight) {
    s.vx *= t.hardLandingRetain;
    s.lastRollGrade = Grade.Miss;
    ev.push(Ev.HardLanding, s.tick, drop, abs(s.vx));
    emitGrade(s, t, ev, GradeKind.Roll, Grade.Miss, drop);
  }
}

/** A grounded tick while rolling: the stick is ignored and the speed kept. */
export function tickRolling(s: SimState, ev: EventBuffer, complete: boolean, moving: boolean): void {
  s.ax = 0;
  s.ay = 0;
  if (!complete) return;
  s.tumbling = 0;
  setState(s, ev, moving || s.vx !== 0 ? S.Run : S.Idle);
}
